<template>
  <div class="product-home-page-container">
    <h2>首页商品展示管理</h2>
    <el-card shadow="hover">
      <div class="card-header">
        <el-button type="primary" @click="handleAddProduct">
          <el-icon><plus /></el-icon> 添加商品到首页
        </el-button>
      </div>
      
      <el-alert
        title="温馨提示"
        type="info"
        description="首页展示区域有限，建议展示10个以内的商品，以获得更佳的用户体验"
        show-icon
        class="tip-alert"
      />
      
      <el-table v-loading="homePageLoading" :data="homePageProducts" border stripe style="width: 100%" row-key="id">
        <el-table-column label="商品信息" min-width="300">
          <template #default="scope">
            <div class="product-info">
              <el-image
                v-if="scope.row.product && scope.row.product.image"
                :src="scope.row.product.image"
                :fit="'cover'"
                style="width: 80px; height: 80px; margin-right: 15px;"
              />
              <div class="product-details">
                <h4 class="product-name">{{ scope.row.product ? scope.row.product.name : '商品已删除' }}</h4>
                <div class="product-price">
                  <p>价格：<span class="price">¥{{ scope.row.product ? scope.row.product.price : '0.00' }}</span></p>
                  <p v-if="scope.row.product && scope.row.product.points_required > 0">
                    积分：<span class="points">{{ scope.row.product.points_required }} 分</span>
                  </p>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="排序" width="150">
          <template #default="scope">
            <el-input-number 
              v-model="scope.row.sort_order" 
              :min="0" 
              :precision="0"
              :step="1"
              @change="handleSortChange(scope.row)"
              style="width: 100%"
              placeholder="请输入"
            />
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="添加时间" width="180" :formatter="formatDate" />
        <el-table-column label="状态" width="150">
          <template #default="scope">
            <div class="status-container">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
                {{ scope.row.status === 1 ? '启用' : '禁用' }}
              </el-tag>
              <el-switch
                :model-value="scope.row.status"
                :active-value="1"
                :inactive-value="0"
                @update:model-value="(value) => handleStatusChange(scope.row, value)"
                style="margin-left: 10px;"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="scope">
            <el-button type="danger" size="small" @click="handleRemoveProduct(scope.row)">
              <el-icon><delete /></el-icon> 移除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    
    <!-- 添加商品到首页抽屉 -->
    <el-drawer
      v-model="addDialogVisible"
      title="添加商品到首页"
      direction="rtl"
      size="50%"
      @close="resetAddForm"
    >
      <el-form :model="addForm" :rules="addRules" ref="addFormRef" label-width="100px">
        <el-form-item label="选择商品" prop="product_ids">
          <div class="search-container">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索商品名称"
              clearable
              @input="handleSearch"
              style="margin-bottom: 15px; width: 100%"
            >
              <template #append>
                <el-button type="primary" @click="handleSearch">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </div>
          <div class="product-table-container">
            <el-table
              v-loading="tableLoading"
              :data="filteredProducts"
              style="width: 100%"
              height="400"
              border
              @selection-change="handleSelectionChange"
              row-key="id"
            >
              <el-table-column
                type="selection"
                width="55"
              />
              <el-table-column
                prop="name"
                label="商品名称"
                min-width="180"
              />
              <el-table-column
                prop="price"
                label="价格"
                width="100"
              >
                <template #default="scope">
                  ¥{{ scope.row.price }}
                </template>
              </el-table-column>
              <el-table-column
                label="排序"
                width="150"
              >
                <template #default="scope">
                  <el-input-number
                    v-model="scope.row.temp_sort_order"
                    :min="0"
                    :precision="0"
                    :step="1"
                    placeholder="请输入"
                    style="width:120px"
                  />
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="addDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmitAddForm">确定</el-button>
        </div>
      </template>
    </el-drawer>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Delete, Search } from '@element-plus/icons-vue';
import request from '../../api/axios';

export default {
  name: 'ProductHomePage',
  components: {
    Plus,
    Delete,
    Search
  },
  setup() {
    const homePageProducts = ref([]);
    const allProducts = ref([]);
    const addDialogVisible = ref(false);
    const addFormRef = ref(null);
    const tableLoading = ref(false);
    const homePageLoading = ref(false);
    const selectedProducts = ref([]);
    const searchKeyword = ref('');
    
    // 筛选后的商品列表
    const filteredProducts = computed(() => {
      if (!searchKeyword.value) {
        return allProducts.value;
      }
      const keyword = searchKeyword.value.toLowerCase();
      return allProducts.value.filter(product => {
        return product.name.toLowerCase().includes(keyword);
      });
    });
    
    // 搜索处理
    const handleSearch = () => {
      // 搜索逻辑已在computed属性中实现
    };
    
    const addForm = ref({
      product_ids: [] // 改为数组，支持多选
    });
    
    const addRules = {
      product_ids: [
        { required: true, message: '请选择商品', trigger: 'change' },
        { type: 'array', min: 1, message: '请至少选择一个商品', trigger: 'change' }
      ]
    };
    
    // 获取首页商品列表
    const fetchHomePageProducts = async () => {
      try {
        homePageLoading.value = true;
        const response = await request({
          url: '/manage/products/home-page',
          method: 'get'
        });
        // 确保homePageProducts始终是数组，防止表格expand功能出错
        let resultData = [];
        if (response && Array.isArray(response.data)) {
          resultData = response.data;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          // 处理嵌套数组结构
          resultData = response.data.data;
        }
        homePageProducts.value = resultData;
      } catch (error) {
        ElMessage.error('获取首页商品列表失败');
        console.error(error);
        // 出错时重置为数组
        homePageProducts.value = [];
      } finally {
        homePageLoading.value = false;
      }
    };
    
    // 获取所有兑换商品列表
    const fetchAllProducts = async () => {
      tableLoading.value = true;
      try {
        const response = await request({
          url: '/manage/products',
          method: 'get',
          params: {
            pageSize: 999,
            status: 'active'
          }
        });
        // 确保response.data.data是数组，防止map函数出错
        let productsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          productsData = response.data.data;
          // 显示所有商品，不进行过滤
        }
        // 初始化每个商品的临时排序号
        allProducts.value = productsData.map(product => ({
          ...product,
          temp_sort_order: 0
        }));
      } catch (error) {
        ElMessage.error('获取商品列表失败');
        console.error(error);
        // 出错时重置为数组
        allProducts.value = [];
      } finally {
        tableLoading.value = false;
      }
    };
    
    // 处理表格选择变化
    const handleSelectionChange = (selection) => {
      selectedProducts.value = selection;
      // 将选中的商品ID同步到表单中
      addForm.value.product_ids = selection.map(item => item.id);
    };
    
    // 页面加载时获取数据
    onMounted(() => {
      fetchHomePageProducts();
      fetchAllProducts();
    });
    
    // 打开添加商品对话框
    const handleAddProduct = () => {
      addDialogVisible.value = true;
    };
    
    // 关闭添加商品对话框时重置表单
    const resetAddForm = () => {
      addForm.value = {
        product_ids: [],
        sort_order: 0
      };
      selectedProducts.value = [];
      if (addFormRef.value) {
        addFormRef.value.resetFields();
      }
    };
    
    // 提交添加商品表单
    const handleSubmitAddForm = async () => {
      if (!addFormRef.value) return;
      
      await addFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            // 遍历选中的商品，逐个添加到首页
            for (const product of selectedProducts.value) {
              await request({
                url: '/manage/products/add-to-home-page',
                method: 'post',
                data: {
                  product_id: product.id,
                  sort_order: product.temp_sort_order || 0
                }
              });
            }
            ElMessage.success('添加成功');
            addDialogVisible.value = false;
            fetchHomePageProducts();
          } catch (error) {
            ElMessage.error('添加失败');
            console.error(error);
          }
        } else {
          return false;
        }
      });
    };
    
    // 从首页移除商品
    const handleRemoveProduct = (product) => {
      ElMessageBox.confirm('确定要从首页移除该商品吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await request({
            url: `/manage/products/remove-from-home-page/${product.id}`,
            method: 'delete'
          });
          ElMessage.success('移除成功');
          fetchHomePageProducts();
        } catch (error) {
          ElMessage.error('移除失败');
          console.error(error);
        }
      }).catch(() => {
        // 取消移除操作
      });
    };
    
    // 更新商品排序
    const handleSortChange = async (product) => {
      try {
        await request({
          url: '/manage/products/update-home-page-sort',
          method: 'put',
          data: {
            items: [{
              id: product.id,
              sort_order: product.sort_order
            }]
          }
        });
        ElMessage.success('排序更新成功');
      } catch (error) {
        ElMessage.error('排序更新失败');
        console.error(error);
        // 恢复原排序值
        fetchHomePageProducts();
      }
    };
    
    // 处理首页商品状态切换
    const handleStatusChange = async (homePageProduct, newStatus) => {
      try {
        // 只有当用户手动改变状态时才调用API，避免页面加载时自动调用
        await request({
          url: `/manage/products/update-home-page-status/${homePageProduct.id}`,
          method: 'put',
          data: {
            status: newStatus
          }
        });
        ElMessage.success('状态更新成功');
        fetchHomePageProducts();
      } catch (error) {
        ElMessage.error('状态更新失败');
        console.error(error);
        // 恢复原状态
        fetchHomePageProducts();
      }
    };
    
    // 格式化日期为 ymd his 格式
    const formatDate = (row, column, cellValue) => {
      if (!cellValue) return '';
      const date = new Date(cellValue);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };
    
    return {
      homePageProducts,
      allProducts,
      filteredProducts,
      addDialogVisible,
      addFormRef,
      addForm,
      addRules,
      tableLoading,
      homePageLoading,
      selectedProducts,
      searchKeyword,
      handleAddProduct,
      resetAddForm,
      handleSubmitAddForm,
      handleRemoveProduct,
      handleSortChange,
      handleStatusChange,
      handleSelectionChange,
      handleSearch,
      formatDate
    };
  }
};
</script>

<style scoped>
.product-home-page-container {
  padding: 20px;
}

.product-home-page-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 20px;
}

.tip-alert {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f0f9eb;
  border-radius: 4px;
}

.product-info {
  display: flex;
  align-items: center;
}

.product-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-name {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.product-price {
  margin: 0;
  font-size: 14px;
  color: #ff4d4f;
}



.price {
  font-weight: bold;
  font-size: 16px;
}

.select-option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.option-product-name {
  font-size: 14px;
  color: #333;
}

.option-product-price {
  font-size: 14px;
  color: #ff4d4f;
  font-weight: bold;
}

/* 搜索容器样式 */
.search-container {
  margin-bottom: 15px;
}

/* 商品表格容器样式 */
.product-table-container {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

/* 抽屉中的表单项样式 */
.el-drawer__body .el-form-item {
  margin-bottom: 20px;
}

/* 抽屉底部按钮区域样式 */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* 状态容器样式 */
.status-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>